import styles from "../../assets/scss/modules/PublicListingSingle.module.scss";
import React, {Fragment, useMemo} from 'react';
import {useSingleListing} from "../context/SingleListingProvider.tsx";
import {displayWebsiteUrl, empty, formatPhoneNumber, officialBrokerPhoto} from "../../utils/utils.ts";
import useCtrBrokers from "../../hooks/useCtrBrokers.ts";
import useCtrValeursFixes from "../../hooks/useCtrValeursFixes.ts";
import {CtrDomainsVf} from "../../graphql/generated/graphql-main.tsx";
import clsx from "clsx";
import useSiteConfig from "../../hooks/useSiteConfig.ts";
import useGfContactForm from "../../hooks/useGfContactForm.ts";
import useReactTexts from "../../hooks/useReactTexts.ts";
import {useWpContext} from "../context/WordPressProvider.tsx";

const cssroot = "xlb";
const ListingBrokers = () => {
    const {others, is_fr} = useWpContext<ZListingSingle>();
    const {__} = useReactTexts();
    const {listing} = useSingleListing();
    const {
        membreByCode,
        bureauByCode,
        firmeByBureau,
        customOfficeName,
        isRestricted,
        isIndexable,
        isEvOffice,
        isMcGillOffice
    } = useCtrBrokers();
    const {fixedValue} = useCtrValeursFixes();
    const {$config} = useSiteConfig();

    // whether current listing is fully restricted based on its brokers
    const restricted = isRestricted(listing);
    // whether current site is allowed to index this displayed listing
    const indexable = isIndexable(listing);

    // handle dynamic filling of values in popup Contact form
    useGfContactForm($config);

    /**
     * Given a broker ID, display its card w/ logo, etc.
     * @param broker
     */
    const renderBrokerCard = (broker: string | null) => {
        // if broker object not found, no point in going further
        const _broker = membreByCode(broker);
        if (_broker == null) return null;

        // if broker is NOT from E&V / McGill offices, hide it (unless 'config.ev_only' is set to FALSE)
        if (($config.ev_only ?? true) && !isEvOffice(_broker.BUR_CODE!) && !isMcGillOffice(_broker.BUR_CODE!)) {
            return null;
        }

        // if broker is from restricted office, hide only that broker
        if ($config.restricted_offices?.includes(_broker.BUR_CODE!)) {
            return null;
        }

        const _office = bureauByCode(_broker?.BUR_CODE);
        const _firme = firmeByBureau(_broker?.BUR_CODE);
        const _phone = formatPhoneNumber(_broker?.TELEPHONE_1
            ?? _broker?.TELEPHONE_2
            ?? _office?.TELEPHONE_1
            ?? _office?.TELEPHONE_2);
        const _email = _broker?.COURRIEL ?? _office?.COURRIEL;
        const _officialPhoto = officialBrokerPhoto(_broker, {isMcgill: $config.is_mcgill});

        return <div className={`${cssroot}__broker card broker mb-8`} key={`broker-card@${broker}`}>
            {/* broker's profile picture */}
            <div className={clsx(`${cssroot}__photo h-80`, styles.broker__photo)}>
                {/* try to load webp first and fallback to centris if none provided */}
                <img className={"!h-full w-auto object-contain"}
                     src={_officialPhoto.url}
                     alt={`${_broker?.PRENOM} ${_broker?.NOM}`}
                     onError={({currentTarget}) => {
                         currentTarget.onerror = null; // prevents looping
                         currentTarget.src = _broker?.PHOTO_URL ?? "";
                     }}
                ></img>
            </div>

            {/* full broker's name */}
            <div className={`${cssroot}__name mt-2 font-bold text-lg broker__name`}>
                {_broker?.PRENOM} {_broker?.NOM}
            </div>
            {/* certificate of the broker */}
            <div className={`${cssroot}__certificate broker__certificate`}>
                {fixedValue(CtrDomainsVf.TypeCertificatMembre, _broker?.TYPE_CERTIFICAT)}&nbsp;
                <span className={"text-[#757575]"}>({_broker?.NO_CERTIFICAT})</span>
            </div>
            <div className={`${cssroot}__company broker__company text-[#757575]`}>
                {_broker?.NOM_SOCIETE ?? ""}
            </div>
            {/* broker's website */}
            {_broker.SITE_WEB && <div className={`${cssroot}__website broker__website`}>
                <a href={_broker.SITE_WEB} target={"_blank"}>
                    {displayWebsiteUrl(_broker.SITE_WEB)}
                </a>
            </div>}
            {/* broker's email address */}
            {_email && <div className={`${cssroot}__email broker__email`}>
                <a href={`mailto:${_email}`} target={"_blank"}>{_email}</a>
            </div>}
            {/* broker's phone number */}
            {_phone && <div className={`${cssroot}__phone broker__phone`}>
                <a href={`tel:${_phone}`} target={"_blank"}>{_phone}</a>
            </div>}


            {/* legal name of agency & office */}
            <div className={`${cssroot}__agency mt-4 broker__agency text-[#757575]`}>
                {__("Agence immobilière")}{is_fr ? " : " : ": "}
            </div>
            <div className={`${cssroot}__office broker__agency`}>
                {_firme?.NOM_LEGAL} <span className={"text-[#757575]"}>({_firme?.NO_CERTIFICAT})</span>
            </div>
            <div className={`${cssroot}__office broker__office`}>
                <span className={"text-[#757575]"}>{__("Boutique immobilière")}</span>
                &nbsp;⬩&nbsp;
                <span className={"text-black"}>
                    {customOfficeName(_office?.CODE!) ?? _office?.NOM_LEGAL}
                </span>
            </div>

        </div>
    }

    // pre-render list of all possible brokers and filter out 'null' ones due to restrictions or other conditions
    const brokerList = useMemo(() => {
        return [
            renderBrokerCard(listing?.COURTIER_INSCRIPTEUR_1 ?? null),
            renderBrokerCard(listing?.COURTIER_INSCRIPTEUR_2 ?? null),
            renderBrokerCard(listing?.COURTIER_INSCRIPTEUR_3 ?? null),
            renderBrokerCard(listing?.COURTIER_INSCRIPTEUR_4 ?? null),
        ].filter(it => it != null);
    }, [restricted, renderBrokerCard]);

    return <Fragment>
        {/* SEO configurations depending on broker restrictions */}
        <link rel={"canonical"} href={window.location.origin + window.location.pathname}/>
        <meta name={"robots"} content={indexable ? "index,follow" : "noindex,nofollow"}/>

        {/* only show 'Brokers' section if at least 1 broker is NON-restricted */}
        {(!restricted && !empty(brokerList)) && (
            <section className={`${cssroot}__root mt-8`}>
                <h3 className={`${cssroot}__header`}>
                    {brokerList.length > 1 ? __("Courtiers immobiliers") : __("Courtier immobilier")}
                </h3>

                <div className={`${cssroot}__brokers mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4`}>
                    {brokerList}
                </div>
            </section>
        )}
    </Fragment>
};

export default ListingBrokers;
